import { useEffect, useMemo, useRef, useState } from "react";
import { api, currency, LOGO_URL, fmtDate } from "@/lib/api";
import { downloadInvoicePdf } from "@/lib/pdf";
import InvoiceDocument from "@/components/InvoiceDocument";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { FilePlus2, Plus, Trash2, ArrowLeft, FileText, Send, Sparkles, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);
const addDays = (dateStr, days) => {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const MANUAL = "__manual__";

export default function CreateInvoice() {
  const [stage, setStage] = useState("start"); // start | build | preview
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);

  const [customerId, setCustomerId] = useState("");
  const [manual, setManual] = useState({ name: "", address: "", email: "", phone: "" });

  const [lineItems, setLineItems] = useState([]);
  const [productPick, setProductPick] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(today());
  const [dueDate, setDueDate] = useState(addDays(today(), 30));
  const [orderNumber, setOrderNumber] = useState("");
  const [notes, setNotes] = useState("");

  const [confirmed, setConfirmed] = useState(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bankDetails, setBankDetails] = useState(null);
  const [companyDetails, setCompanyDetails] = useState(null);
  const pdfRef = useRef(null);

  useEffect(() => {
    api.get("/customers").then(({ data }) => setCustomers(data));
    api.get("/products").then(({ data }) => setProducts(data));
    api.get("/settings").then(({ data }) => {
      setBankDetails(data.bank_details);
      setCompanyDetails(data.company_details);
    });
  }, []);

  const isManual = customerId === MANUAL;

  const customerData = useMemo(() => {
    if (isManual) return manual;
    const c = customers.find((x) => x.id === customerId);
    return c ? { name: c.name, address: c.address, email: c.email, phone: c.phone, tag: c.tag || "" } : null;
  }, [customerId, isManual, manual, customers]);

  const addProduct = (id) => {
    const p = products.find((x) => x.id === id);
    if (!p) return;
    setLineItems((prev) => [
      ...prev,
      {
        title: p.title,
        variant: p.variants?.[0] || "",
        variants: p.variants || [],
        quantity: 1,
        price: p.price,
        cost_price: p.cost_price_excl || 0,
        vat_applicable: p.vat_applicable,
      },
    ]);
    setProductPick("");
  };

  const updateItem = (i, patch) =>
    setLineItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const removeItem = (i) => setLineItems((prev) => prev.filter((_, idx) => idx !== i));

  const totals = useMemo(() => {
    let subtotal = 0;
    let vat = 0;
    let cost = 0;
    for (const it of lineItems) {
      const qty = parseInt(it.quantity) || 0;
      const line = (parseFloat(it.price) || 0) * qty;
      subtotal += line;
      cost += (parseFloat(it.cost_price) || 0) * qty;
      if (it.vat_applicable) vat += line * 0.2;
    }
    const r = (n) => Math.round(n * 100) / 100;
    return {
      subtotal: r(subtotal),
      vat: r(vat),
      total: r(subtotal + vat),
      cost: r(cost),
      profit: r(subtotal - cost),
    };
  }, [lineItems]);

  const goPreview = () => {
    if (!customerData || !customerData.name?.trim())
      return toast.error("Please select or enter a customer");
    if (lineItems.length === 0) return toast.error("Add at least one product");
    setStage("preview");
  };

  const confirmInvoice = async () => {
    setSaving(true);
    try {
      const payload = {
        customer_name: customerData.name,
        customer_address: customerData.address || "",
        customer_email: customerData.email || "",
        customer_phone: customerData.phone || "",
        customer_tag: customerData.tag || "",
        customer_id: isManual ? null : customerId,
        order_number: orderNumber,
        line_items: lineItems.map((it) => ({
          title: it.title,
          variant: it.variant || "",
          quantity: parseInt(it.quantity) || 1,
          price: parseFloat(it.price) || 0,
          cost_price: parseFloat(it.cost_price) || 0,
          vat_applicable: it.vat_applicable,
        })),
        invoice_date: invoiceDate,
        due_date: dueDate,
        notes,
      };
      const { data } = await api.post("/invoices", payload);
      setConfirmed(data);
      setPopupOpen(true);
      toast.success(`Invoice ${data.invoice_number} confirmed`);
    } catch {
      toast.error("Failed to confirm invoice");
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setStage("start");
    setCustomerId("");
    setManual({ name: "", address: "", email: "", phone: "" });
    setLineItems([]);
    setInvoiceDate(today());
    setDueDate(addDays(today(), 30));
    setOrderNumber("");
    setNotes("");
    setConfirmed(null);
    setPopupOpen(false);
  };

  // ---------- START ----------
  if (stage === "start") {
    return (
      <div className="max-w-6xl mx-auto" data-testid="create-invoice-page">
        <h1 className="font-heading text-3xl font-bold text-[#032352]">Create an Order</h1>
        <p className="text-[#5B5C5F] mt-1 mb-10">Generate a professional invoice in a few steps.</p>
        <div className="flex flex-col items-center justify-center bg-white border border-slate-200 rounded-lg py-20">
          <img src={LOGO_URL} alt="J&L" className="h-24 object-contain mb-6" />
          <Button
            data-testid="start-create-invoice-button"
            onClick={() => setStage("build")}
            className="bg-[#032352] hover:bg-[#04347A] h-12 px-8 text-base"
          >
            <FilePlus2 className="w-5 h-5 mr-2" /> Create an Order
          </Button>
        </div>
      </div>
    );
  }

  // ---------- PREVIEW ----------
  if (stage === "preview") {
    return (
      <div className="max-w-4xl mx-auto" data-testid="invoice-preview-page">
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="ghost"
            data-testid="back-to-edit-button"
            onClick={() => setStage("build")}
            className="text-[#5B5C5F]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Edit
          </Button>
          <span className="text-sm text-[#5B5C5F]">Provisional Invoice — prices are editable</span>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-4 sm:p-8">
          <div className="flex justify-between items-start pb-6 border-b-2" style={{ borderColor: "#032352" }}>
            <div className="max-w-xs">
              <img src={LOGO_URL} alt="J&L" className="h-20 object-contain mb-3" />
              {companyDetails && (
                <div className="text-xs text-[#5B5C5F] leading-relaxed">
                  <p className="font-semibold text-[#032352] text-sm">
                    {companyDetails.name || "J&L Industrial Supplies"}
                  </p>
                  {companyDetails.address && (
                    <p className="whitespace-pre-line">{companyDetails.address}</p>
                  )}
                  {companyDetails.phone && <p>Tel: {companyDetails.phone}</p>}
                  {companyDetails.email && <p>{companyDetails.email}</p>}
                  {companyDetails.website && <p>{companyDetails.website}</p>}
                  {companyDetails.vat_number && <p>VAT No: {companyDetails.vat_number}</p>}
                  {companyDetails.company_number && <p>Company No: {companyDetails.company_number}</p>}
                </div>
              )}
            </div>
            <div className="text-right">
              <h2 className="font-heading text-3xl font-extrabold text-[#032352]">PROVISIONAL</h2>
              <p className="text-sm text-[#5B5C5F]">Confirm to finalise & get invoice number</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6 py-6">
            <div>
              <p className="text-xs uppercase tracking-wider text-[#5B5C5F] mb-2">Bill To</p>
              <p className="font-semibold text-[#032352]">{customerData.name}</p>
              {customerData.address && (
                <p className="text-sm text-[#5B5C5F] whitespace-pre-line">{customerData.address}</p>
              )}
              {customerData.email && <p className="text-sm text-[#5B5C5F]">{customerData.email}</p>}
              {customerData.phone && <p className="text-sm text-[#5B5C5F]">{customerData.phone}</p>}
            </div>
            <div className="text-right space-y-1">
              <div className="flex justify-between gap-8">
                <span className="text-xs uppercase tracking-wider text-[#5B5C5F]">Invoice Date</span>
                <span className="text-sm font-medium text-[#032352]">{fmtDate(invoiceDate)}</span>
              </div>
              <div className="flex justify-between gap-8">
                <span className="text-xs uppercase tracking-wider text-[#5B5C5F]">Due Date</span>
                <span className="text-sm font-medium text-[#032352]">{fmtDate(dueDate)}</span>
              </div>
              {orderNumber && (
                <div className="flex justify-between gap-8">
                  <span className="text-xs uppercase tracking-wider text-[#5B5C5F]">Order No</span>
                  <span className="text-sm font-medium text-[#032352]">{orderNumber}</span>
                </div>
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[680px]">
            <thead>
              <tr style={{ backgroundColor: "#032352" }} className="text-white">
                <th className="text-left p-3 font-medium">Description</th>
                <th className="text-center p-3 font-medium w-20">Qty</th>
                <th className="text-right p-3 font-medium w-32">Unit Price (£)</th>
                <th className="text-right p-3 font-medium w-32 bg-[#5B5C5F]">Cost (£)</th>
                <th className="text-center p-3 font-medium w-16">VAT</th>
                <th className="text-right p-3 font-medium w-28">Amount</th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((it, i) => (
                <tr key={i} className="border-b border-slate-200" data-testid={`preview-line-${i}`}>
                  <td className="p-3 text-[#032352]">
                    {it.title}
                    {it.variant ? <span className="text-[#5B5C5F]"> — {it.variant}</span> : null}
                  </td>
                  <td className="p-2 text-center">
                    <Input
                      type="number"
                      min="1"
                      data-testid={`preview-qty-${i}`}
                      value={it.quantity}
                      onChange={(e) => updateItem(i, { quantity: e.target.value })}
                      className="h-9 w-16 text-center font-mono-num mx-auto"
                    />
                  </td>
                  <td className="p-2 text-right">
                    <Input
                      type="number"
                      step="0.01"
                      data-testid={`preview-price-${i}`}
                      value={it.price}
                      onChange={(e) => updateItem(i, { price: e.target.value })}
                      className="h-9 w-28 text-right font-mono-num ml-auto"
                    />
                  </td>
                  <td className="p-2 text-right bg-slate-50">
                    <Input
                      type="number"
                      step="0.01"
                      data-testid={`preview-cost-${i}`}
                      value={it.cost_price}
                      onChange={(e) => updateItem(i, { cost_price: e.target.value })}
                      className="h-9 w-28 text-right font-mono-num ml-auto"
                    />
                  </td>
                  <td className="p-3 text-center text-xs text-[#5B5C5F]">
                    {it.vat_applicable ? "20%" : "—"}
                  </td>
                  <td className="p-3 text-right font-mono-num text-[#032352]">
                    {currency((parseFloat(it.price) || 0) * (parseInt(it.quantity) || 0))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>

          <div className="flex flex-col md:flex-row justify-between gap-4 mt-6">
            <div
              className="rounded-md border border-dashed border-[#5B5C5F]/40 bg-slate-50 p-4 w-full md:w-72"
              data-testid="preview-profit-panel"
            >
              <p className="text-xs uppercase tracking-wider text-[#5B5C5F] mb-2">
                Internal · not shown on invoice
              </p>
              <div className="flex justify-between text-sm">
                <span className="text-[#5B5C5F]">Total Cost</span>
                <span className="font-mono-num text-[#5B5C5F]" data-testid="preview-cost-total">
                  {currency(totals.cost)}
                </span>
              </div>
              <div className="flex justify-between text-sm mt-1">
                <span className="text-[#5B5C5F]">Profit</span>
                <span
                  className={`font-mono-num font-semibold ${
                    totals.profit >= 0 ? "text-emerald-600" : "text-red-500"
                  }`}
                  data-testid="preview-profit"
                >
                  {currency(totals.profit)}
                </span>
              </div>
            </div>

            <div className="w-full md:w-72 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-[#5B5C5F]">Subtotal</span>
                <span className="font-mono-num text-[#032352]" data-testid="preview-subtotal">
                  {currency(totals.subtotal)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#5B5C5F]">VAT (20%)</span>
                <span className="font-mono-num text-[#032352]" data-testid="preview-vat">
                  {currency(totals.vat)}
                </span>
              </div>
              <div
                className="flex justify-between items-center pt-2 border-t-2 mt-2"
                style={{ borderColor: "#032352" }}
              >
                <span className="font-heading font-bold text-[#032352]">Total Due</span>
                <span className="font-mono-num font-bold text-lg text-[#032352]" data-testid="preview-total">
                  {currency(totals.total)}
                </span>
              </div>
            </div>
          </div>

          {bankDetails &&
            (bankDetails.account_number || bankDetails.account_name || bankDetails.iban) && (
              <div className="mt-8 pt-4 border-t border-slate-200" data-testid="preview-bank-details">
                <p className="text-xs uppercase tracking-wider text-[#5B5C5F] mb-2">Payment Details</p>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm text-[#032352] max-w-md">
                  {bankDetails.account_name && (
                    <>
                      <span className="text-[#5B5C5F]">Account Name</span>
                      <span className="font-medium">{bankDetails.account_name}</span>
                    </>
                  )}
                  {bankDetails.bank_name && (
                    <>
                      <span className="text-[#5B5C5F]">Bank</span>
                      <span className="font-medium">{bankDetails.bank_name}</span>
                    </>
                  )}
                  {bankDetails.sort_code && (
                    <>
                      <span className="text-[#5B5C5F]">Sort Code</span>
                      <span className="font-mono-num font-medium">{bankDetails.sort_code}</span>
                    </>
                  )}
                  {bankDetails.account_number && (
                    <>
                      <span className="text-[#5B5C5F]">Account Number</span>
                      <span className="font-mono-num font-medium">{bankDetails.account_number}</span>
                    </>
                  )}
                  {bankDetails.iban && (
                    <>
                      <span className="text-[#5B5C5F]">IBAN</span>
                      <span className="font-mono-num font-medium">{bankDetails.iban}</span>
                    </>
                  )}
                </div>
              </div>
            )}
        </div>

        <div className="flex justify-end mt-6">
          <Button
            data-testid="confirm-invoice-button"
            onClick={confirmInvoice}
            disabled={saving}
            className="bg-[#032352] hover:bg-[#04347A] h-11 px-8"
          >
            <CheckCircle2 className="w-5 h-5 mr-2" /> Confirm Order
          </Button>
        </div>

        <PostConfirmDialog
          open={popupOpen}
          onOpenChange={setPopupOpen}
          invoice={confirmed}
          onDone={reset}
          onGeneratePdf={() =>
            downloadInvoicePdf(pdfRef.current, `Invoice-${confirmed?.invoice_number}.pdf`)
          }
        />

        {/* Off-screen render used only for PDF capture */}
        {confirmed && (
          <div style={{ position: "absolute", left: "-10000px", top: 0, width: "794px" }} aria-hidden>
            <div ref={pdfRef}>
              <InvoiceDocument invoice={confirmed} />
            </div>
          </div>
        )}
      </div>
    );
  }

  // ---------- BUILD ----------
  return (
    <div className="max-w-4xl mx-auto" data-testid="invoice-build-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-[#032352]">New Invoice</h1>
          <p className="text-[#5B5C5F] mt-1">Select a customer, add products, then generate.</p>
        </div>
        <Button variant="ghost" onClick={() => setStage("start")} className="text-[#5B5C5F]">
          <ArrowLeft className="w-4 h-4 mr-2" /> Cancel
        </Button>
      </div>

      {/* Customer */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 mb-6">
        <h3 className="font-heading font-semibold text-[#032352] mb-4">1. Customer</h3>
        <Label>Select an account</Label>
        <Select value={customerId} onValueChange={setCustomerId}>
          <SelectTrigger data-testid="select-customer" className="mb-4">
            <SelectValue placeholder="Choose an account or enter manually" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={MANUAL}>✍️ Enter manually</SelectItem>
            {customers.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {isManual && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="manual-customer-fields">
            <div className="col-span-2">
              <Label>Name / Business Name</Label>
              <Input
                data-testid="manual-name"
                value={manual.name}
                onChange={(e) => setManual({ ...manual, name: e.target.value })}
              />
            </div>
            <div className="col-span-2">
              <Label>Address</Label>
              <Textarea
                data-testid="manual-address"
                value={manual.address}
                onChange={(e) => setManual({ ...manual, address: e.target.value })}
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                data-testid="manual-email"
                value={manual.email}
                onChange={(e) => setManual({ ...manual, email: e.target.value })}
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                data-testid="manual-phone"
                value={manual.phone}
                onChange={(e) => setManual({ ...manual, phone: e.target.value })}
              />
            </div>
          </div>
        )}
      </div>

      {/* Products */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 mb-6">
        <h3 className="font-heading font-semibold text-[#032352] mb-4">2. Products</h3>
        <div className="flex gap-2 mb-4">
          <Select value={productPick} onValueChange={setProductPick}>
            <SelectTrigger data-testid="select-product" className="flex-1">
              <SelectValue placeholder="Select a product to add" />
            </SelectTrigger>
            <SelectContent>
              {products.length === 0 && (
                <div className="px-3 py-2 text-sm text-[#5B5C5F]">No products — add some first</div>
              )}
              {products.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.title} — {currency(p.price)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            data-testid="add-line-item-button"
            onClick={() => addProduct(productPick)}
            disabled={!productPick}
            className="bg-[#032352] hover:bg-[#04347A]"
          >
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        </div>

        {lineItems.length === 0 ? (
          <p className="text-sm text-[#5B5C5F] text-center py-6">No products added yet.</p>
        ) : (
          <div className="space-y-2">
            {lineItems.map((it, i) => (
              <div
                key={i}
                data-testid={`build-line-${i}`}
                className="flex flex-wrap items-center gap-3 border border-slate-200 rounded-md p-3"
              >
                <div className="flex-1">
                  <p className="font-medium text-[#032352]">{it.title}</p>
                  <p className="text-xs text-[#5B5C5F]">{currency(it.price)} each</p>
                </div>
                {it.variants?.length > 0 && (
                  <Select value={it.variant} onValueChange={(v) => updateItem(i, { variant: v })}>
                    <SelectTrigger data-testid={`build-variant-${i}`} className="w-full sm:w-40">
                      <SelectValue placeholder="Variant" />
                    </SelectTrigger>
                    <SelectContent>
                      {it.variants.map((v) => (
                        <SelectItem key={v} value={v}>
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Input
                  type="number"
                  min="1"
                  data-testid={`build-qty-${i}`}
                  value={it.quantity}
                  onChange={(e) => updateItem(i, { quantity: e.target.value })}
                  className="w-20 text-center"
                />
                <button
                  data-testid={`remove-line-${i}`}
                  onClick={() => removeItem(i)}
                  className="text-slate-400 hover:text-red-500 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Dates */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 mb-6">
        <h3 className="font-heading font-semibold text-[#032352] mb-4">3. Dates & Notes</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Invoice Date</Label>
            <Input
              type="date"
              data-testid="invoice-date-input"
              value={invoiceDate}
              onChange={(e) => {
                setInvoiceDate(e.target.value);
                setDueDate(addDays(e.target.value, 30));
              }}
            />
          </div>
          <div>
            <Label>Due Date</Label>
            <Input
              type="date"
              data-testid="due-date-input"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </div>
          <div className="col-span-2">
            <Label>Order Number</Label>
            <Input
              data-testid="order-number-input"
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value)}
              placeholder="Customer's order / PO number"
            />
          </div>
          <div className="col-span-2">
            <Label>Notes (optional)</Label>
            <Textarea
              data-testid="invoice-notes-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Payment terms, PO number, etc."
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          data-testid="generate-provisional-button"
          onClick={goPreview}
          className="bg-[#032352] hover:bg-[#04347A] h-11 px-8"
        >
          Generate Provisional Invoice
        </Button>
      </div>
    </div>
  );
}

function PostConfirmDialog({ open, onOpenChange, invoice, onDone, onGeneratePdf }) {
  const soon = (label) => toast.info(`${label} — coming soon`);
  const [downloading, setDownloading] = useState(false);
  const handlePdf = async () => {
    setDownloading(true);
    try {
      await onGeneratePdf();
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("Could not generate PDF");
    } finally {
      setDownloading(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="post-confirm-dialog">
        <DialogHeader>
          <div className="flex justify-center mb-2">
            <CheckCircle2 className="w-12 h-12 text-emerald-500" />
          </div>
          <DialogTitle className="text-center font-heading text-[#032352]">
            Invoice {invoice?.invoice_number} Confirmed
          </DialogTitle>
          <DialogDescription className="text-center">
            What would you like to do next?
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 py-2">
          <Button
            data-testid="generate-pdf-button"
            onClick={handlePdf}
            disabled={downloading}
            className="bg-[#032352] hover:bg-[#04347A] justify-start h-12"
          >
            <FileText className="w-5 h-5 mr-3" /> {downloading ? "Generating…" : "Download PDF Invoice"}
          </Button>
          <Button
            data-testid="send-invoice-button"
            onClick={() => soon("Send Invoice")}
            variant="outline"
            className="justify-start h-12 border-[#032352] text-[#032352]"
          >
            <Send className="w-5 h-5 mr-3" /> Send Invoice
          </Button>
          <Button
            data-testid="generate-send-button"
            onClick={() => soon("Generate & Send")}
            variant="outline"
            className="justify-start h-12 border-[#032352] text-[#032352]"
          >
            <Sparkles className="w-5 h-5 mr-3" /> Generate &amp; Send
          </Button>
        </div>
        <Button variant="ghost" onClick={onDone} data-testid="done-button" className="text-[#5B5C5F]">
          Done
        </Button>
      </DialogContent>
    </Dialog>
  );
}
